/**
 * Mask Directive
 *
 * @since: 2021-02-01
 * @author: jungmin87.kim
 * https://github.com/vuejs-community/vue-directive-mask/tree/939600d470554d43bb75cf544eaeb2ce8ce2041d
 * Usege: v-mask="####-##-## ##:##"
 * Key
 *  - #: Number
 *  - A: Latin letter
 *  - N: Number and latin letter
 *  - X: Any
 */
const maskStart = /^([^#ANX]+)/
const maskReg = /^([#ANX])$/

/**
 * value 값이 mask 포맷에 일치하는지 확인
 *
 * @param {string} val
 * @param {string} mask
 * @returns boolean
**/
function isMatchVal (val, mask) {
  if (val.length !== mask.length) return false
  let isMatch = true
  const maskArr = mask.split('')
  maskArr.forEach((maskChar, idx) => {
    const valChar = val[idx]
    if (valChar !== maskChar && !isMatchChar(valChar, maskChar)) isMatch = false
  })
  return isMatch
}

/**
 * 한 글자의 value 값이 mask 값에 일치하는지 확인
 *
 * @param {string} valChar
 * @param {string} maskChar
 * @returns boolean
**/
function isMatchChar (valChar, maskChar) {
  if (maskChar === '#') {
    return /[0-9]/.test(valChar)
  } else if (maskChar === 'A') {
    return /[a-z]/i.test(valChar)
  } else if (maskChar === 'N') {
    return /[a-z0-9]/i.test(valChar)
  } else if (maskChar === 'X') return true
  return false
}

/**
 * Input에서 Input Element를 추출
 *
 * @param {HTMLElement} element
 * @returns HTMLElement input Element
**/
function getInputElement (element) {
  if (element.tagName.toLocaleUpperCase() === 'INPUT') {
    return element
  }

  const inputElements = element.getElementsByTagName('input')
  if (inputElements.length === 0) {
    return
  }

  return inputElements[0]
}

/**
 * 입력값을 Mask 포맷에 맞도록 제어
 *  1. 커서의 정보를 가져온다
 *  2. parseValue 함수로 값과 커서의 위치를 보정한다.
 *  3. format 함수로 mask와 value값을 한번 더 보정
 *
 * @param {HTMLElement} element
 * @param {boolean} force
**/
async function updateValue (element, force = false) {
  let {value, dataset: {prevValue = '', mask}} = element
  if (force || (value && value !== prevValue && value.length > prevValue.length)) {
    const cursorIdx = getCursorPos(element) || -1
    const parseData = parseValue(value, mask, cursorIdx) // 값과 커서의 위치 보정
    element.value = format(parseData.value, mask)
    element.dataset.prevValue = element.value
    if (document.activeElement === element) { // input 에 focus 상태일때만 커서 위치 변경
      setSelectionRange(element, parseData.pos, parseData.pos)
    }
    const event = document.createEvent('HTMLEvents')
    event.initEvent('input', true, true)
    element.dispatchEvent(event)
  } else {
    element.dataset.prevValue = value
  }
}

/**
 * mask값을 비교 대조하여 보정
 *
 * @param {string} data Input Component의 값
 * @param {string} mask mask 값
 * @returns string mask값과 대조한결과 값
**/
function format (data, mask = '') {
  if (!mask.length) {
    return data
  }

  if (data.length === 1 && maskStart.test(mask)) {
    data = maskStart.exec(mask)[0] + data
  }

  let dataOffset = 0
  let dataOutput = ''
  let shouldContinue = true

  for (let i = 0; i < mask.length && shouldContinue; i++) {
    const dataChar = data.charAt(i + dataOffset)
    const maskChar = mask.charAt(i)

    if (maskChar === '#') {
      if (/[0-9]/.test(dataChar)) dataOutput += dataChar
      else shouldContinue = false
    } else if (maskChar === 'A') {
      if (/[a-z]/i.test(dataChar)) dataOutput += dataChar
      else shouldContinue = false
    } else if (maskChar === 'N') {
      if (/[a-z0-9]/i.test(dataChar)) dataOutput += dataChar
      else shouldContinue = false
    } else if (maskChar === '?') {
      dataOffset++
    } else if (maskChar === 'X') {
      dataOutput += dataChar
    } else {
      dataOutput += maskChar
      if (dataChar && dataChar !== maskChar) dataOffset++
    }
  }
  return dataOutput
}

/**
 * 값 보정
 *  - 커서의 위치가 중간에 있을 때에만 보정, 끝에 있을 경우 보정 X
 *  - 입력시에 커서의 위치에서 입력시에 치환
 *  - mask와 비교하여 포맷에서 벗어낫을 경우 보정
 *  - 값이 모두 채워진 상태에서 중간값을 입력시 치환
 *  - 값이 모두 채워지지 않은 상태에서 중간값을 입력시 보충
 *
 * @param {string} value
 * @param {string} mask
 * @param {number} cursorIdx
 * @return {value, pos} 보정된 값 및 커서의 위치를 수정
**/
function parseValue (value, mask, cursorIdx) {
  let valArr = value.split('')
  const maskArr = mask.split('')

  const selectVal = value[cursorIdx - 1]
  const maskIdx = maskArr.findIndex((m, idx) => {
    return (cursorIdx - 1) <= idx && maskReg.test(m)
  })
  const selectMask = maskIdx >= 0 ? mask[maskIdx] : ''
  // 입력 부분 치환
  if (!isMatchVal(valArr.join(''), mask) && valArr.length - 1 === maskArr.length) { // mask 유형과 다르며 값을 치환할 때 보정
    if (!isMatchChar(selectVal, selectMask)) { // 입력된 값이 Mask 유형과 다를 시
      valArr.splice(cursorIdx - 1, 1)
      cursorIdx--
    } else if (maskReg.test(selectMask) && cursorIdx === (maskIdx + 1)) {
      valArr.splice(cursorIdx - 1, 1)
      valArr[cursorIdx - 1] = selectVal
    } else { // 입력된 값이 Mask 파라미터가 아닐 시 보정
      if (maskIdx >= 0) {
        valArr[maskIdx + 1] = selectVal
        valArr.splice(cursorIdx - 1, 1)
        cursorIdx = maskIdx + 1
      }
    }
  } else if (isMatchChar(selectVal, selectMask) && !isMatchChar(selectVal, mask[cursorIdx - 1]) && valArr.length === maskArr.length) { // 입력 부분값이 #ANX 유형이 아니며 전체 입력값과 마스크의 길이가 같으면 치환
    valArr.splice(cursorIdx - 1, 1, mask[cursorIdx - 1])
    valArr.splice(cursorIdx, 1, selectVal)
    cursorIdx = maskIdx + 1
  } else if (isMatchChar(selectVal, selectMask) && cursorIdx - 1 !== maskIdx) { // 커서 위치 보정
    cursorIdx = maskIdx + 1
  }

  // mask 보정
  if (isMatchVal(valArr.join(''), mask)) return { value: valArr.join(''), pos: cursorIdx }
  let valIdx = 0
  const tempArr = []
  for (let i = 0; i < mask.length; i++) {
    const maskChar = mask[i]
    if (!maskReg.test(maskChar)) {
      tempArr.push(maskChar)
      continue
    }
    for (let j = valIdx; j < valArr.length; j++) {
      const valChar = valArr[j]
      if (isMatchChar(valChar, maskChar)) {
        tempArr.push(valChar)
        valIdx++
        break
      } else {
        valIdx++
      }
    }
  }
  valArr = tempArr

  return { value: valArr.join(''), pos: cursorIdx }
}

/**
 * element에 mask값을 업데이트
 *
 * @param {HTMLElement} element
 * @param {string} mask
**/
function updateMask (element, mask) {
  element.dataset.mask = mask
}

/**
 * 입력 이벤트가 발생할 시에 updateValue를 수행
 *
 * @param {object} event
**/
function onInputListener (event) {
  updateValue(event.target)
}

/**
 * 현재 포커스 위치
 *
 * @param {HTMLElement} el input element
 * @returns Number 커서 위치
**/
function getCursorPos (el) {
  let iCarePos = 0
  // IE Support
  if (document.selection) {
    el.focus()
    const sel = document.selection.createRange()
    sel.moveStart('character', -el.value.length)
    iCarePos = sel.text.length
  } else if (el.selectionStart || el.selectionStart === '0') {
    iCarePos = el.selectionStart
  }
  return iCarePos
}

/**
 * 포커스 위치 변경
 *
 * @param {HTMLElement} el input element
 * @param {Number} startIdx 커서 시작
 * @param {Number} endIdx 커서 끝
**/
function setSelectionRange (el, startIdx, endIdx) {
  if (el.setSelectionRange) {
    el.focus()
    el.setSelectionRange(startIdx, endIdx)
  } else if (el.createTextRange) {
    const range = el.createTextRange()
    range.collapse(true)
    range.moveEnd('charactor', endIdx)
    range.moveStart('charactor', startIdx)
    range.select()
  }
}

export default {
  name: 'mask',
  /**
   * 컴포넌트 생성시
   * 1. 입력 Element에 mask, prevValue 속성값을 주입
   * 2. 입력 Element에 keydown 이벤트를 선언하며 입력이 발생시 onInputListener 수행
   *
   * @param {HTMLElement} element
   * @param {Object} binding
  **/
  bind (element, binding) {
    const inputElement = getInputElement(element)

    updateMask(inputElement, binding.value)
    updateValue(inputElement)
    inputElement.addEventListener('input', onInputListener)
  },
  /**
   * 컴포넌트 소멸시
   * 입력 Element에 keydown 이벤트를 제거
   *
   * @param {HTMLElement} element
  **/
  unbind (element) {
    const inputElement = getInputElement(element)
    if (!inputElement) return
    inputElement.removeEventListener('keydown', onInputListener)
  },
  /**
   * 컴포넌트에 변경사항이 있을 경우
   * 입력 Element에 기존 keydown 이벤트를 제거하고 신규로 선언하며 입력이 발생시 onInputListener 수행
   *
   * @param {HTMLElement} element
   * @param {object} binding
   **/
  componentUpdated (element, binding) {
    const inputElement = getInputElement(element)

    if (binding.value !== binding.oldValue) {
      updateMask(inputElement, binding.value)
    }

    updateValue(inputElement)
    inputElement.removeEventListener('keydown', onInputListener)
    inputElement.addEventListener('keydown', onInputListener)
  }
}
